import { Divider } from 'src/design/atoms/Divider';
import { Grid } from 'src/design/atoms/Grid';
import { Group } from 'src/design/atoms/Group';
import { Input } from 'src/design/atoms/Input';
import { Paper } from 'src/design/atoms/Paper';
import { SimpleGrid } from 'src/design/atoms/SimpleGrid';
import { Stack } from 'src/design/atoms/Stack';
import { Text } from 'src/design/atoms/Text';
import { Textarea } from 'src/design/atoms/Textarea';
import { TextInput } from 'src/design/atoms/TextInput';
import { SecondaryButton } from 'src/design/atoms/SecondaryButton';
import { usePdfBlobUrl } from 'src/design/molecules/usePdfBlobUrl';
import { useFormik } from 'formik';
import React from 'react';
import {
  FileResponse,
  JobDto,
  NotificationCompositionRequest,
  ResourceAttachmentDto,
  TransmittalDto,
  TransmittalRequestDto,
  UserDto,
} from 'src/api/FiretrolApiClient';
import {
  usePreviewTransmittalPdf,
  useRotateTransmittalExternalAccess,
  useSaveTransmittal,
  useUploadTransmittalTempFile,
} from 'src/api/queries/transmittals/useTransmittalMutations';
import {
  useUpdateAttachmentComment,
  useUploadTempAttachment,
} from 'src/api/queries/workflowModules/useAttachmentMutations';
import { IContact } from 'src/api/models/Organization';
import { contactDtoToIContact } from 'src/app/workflows/contactDtoToIContact';
import { FileDrop } from 'src/design/organisms/FileDrop/FileDrop';
import { applyAttachmentChange } from 'src/design/organisms/attachments/attachmentEdits';
import {
  IAttachedFile,
  IAttachmentChange,
} from 'src/design/organisms/attachments/attachmentTypes';
import { ContactPicker } from 'src/app/selectors/ContactPicker';
import { SubmittedBySelector } from 'src/app/selectors/SubmittedBySelector';
import { useWorkflowState } from 'src/app/workflows/WorkflowButtons/useWorkflowState';
import { isStepEditable } from 'src/app/workflows/isStepEditable';
import { useNotificationComposer } from 'src/hooks/useNotificationComposer';
import { WorkflowFormFooter } from 'src/app/workflows/WorkflowFormFooter';
import { shouldOpenTransitionComposer } from 'src/app/workflows/shouldOpenTransitionComposer';
import { isTerminalStatus } from 'src/app/workflows/workflowLifecycle';
import { useAuth } from 'src/hooks/useAuth';
import { useWorkflowSubmission } from 'src/hooks/useWorkflowSubmission';
import {
  characterCount,
  FIELD_LIMITS,
  getErrorMessage,
  logger,
  showErrorToast,
  showToast,
} from 'src/utils';
import * as Yup from 'yup';
import { RelatedFeedFooter } from 'src/routes/Jobs/ActivityFeed/components/RelatedFeedFooter';
import { useRevokeExternalLinks } from 'src/app/workflows/useRevokeExternalLinks';
import { TransmittalDocumentPicker } from './TransmittalDocumentPicker';
import { useWorkflowFormValidation } from 'src/app/workflows/useWorkflowFormValidation';
import { useResolveAttachmentUrl } from 'src/api/queries/attachments/useAttachmentMutations';

const SIGNED_CATEGORY = 'signed';

const TRANSMITTAL_FIELD_LABELS: Record<string, string> = {
  title: 'Title',
  notes: 'Notes',
  documents: 'Documents',
};

const validationSchema = Yup.object().shape({
  title: Yup.string()
    .nullable()
    .max(
      FIELD_LIMITS.singleLine,
      `Title must be ${FIELD_LIMITS.singleLine} characters or fewer`,
    )
    .when('id', {
      is: (id?: string) => !id,
      then: (schema) =>
        schema.required('Title is required').min(1, 'Title is required'),
    }),
  documents: Yup.array()
    .min(1, 'Attach at least one document')
    .required('Attach at least one document'),
  notes: Yup.string()
    .nullable()
    .max(
      FIELD_LIMITS.narrative,
      `Notes must be ${FIELD_LIMITS.narrative} characters or fewer`,
    ),
});

/** Which slice of the form to render; omit to render the full form (create flow). */
export type TransmittalFormSection =
  | 'details'
  | 'attachments'
  | 'signed'
  | 'notifications'
  | 'history'
  | 'emails';

export interface TransmittalFormValues
  extends Omit<TransmittalDto, 'externalContact'> {
  externalContact: IContact | null;
  filesToAdd: ResourceAttachmentDto[];
  filesToRemove: ResourceAttachmentDto[];
}

interface ITransmittalFormProps {
  initialValues: TransmittalFormValues;
  selectedJob: JobDto;
  /** In ViewTransmittal the form is split across tabs; this selects the visible slice. Omit for the single-column create flow. */
  activeSection?: TransmittalFormSection;
  jobUsers?: UserDto[];
  onSaved?: (transmittal: TransmittalDto) => void;
  onCreated?: (transmittal: TransmittalDto) => void;
  /** Content for non-form tabs (notifications, history, emails) rendered above the persistent footer. */
  children?: React.ReactNode;
}

interface IPendingTransition {
  status: string;
  stepIndex: number;
  stepLabel: string;
}

export const TransmittalForm: React.FC<ITransmittalFormProps> = ({
  onSaved,
  onCreated,
  initialValues,
  selectedJob,
  activeSection,
  jobUsers = [],
  children,
}) => {
  const { isAdmin } = useAuth();
  const formik = useFormik<TransmittalFormValues>({
    initialValues,
    validationSchema,
    validateOnMount: true,
    onSubmit: () => {},
  });

  const { values, setFieldValue } = formik;

  const [submittingToContact, setSubmittingToContact] =
    React.useState<IContact | null>(
      contactDtoToIContact(
        initialValues.submittedToContact,
        initialValues.submittedToContact?.id,
      ),
    );

  const { currentStep } = useWorkflowState(
    values.workflow ?? null,
    values.stepIndex ?? -1,
  );

  const composer = useNotificationComposer(
    'transmittal',
    selectedJob.id,
    values.id ?? '',
  );
  const [composerOpen, setComposerOpen] = React.useState(false);
  const [pendingTransition, setPendingTransition] =
    React.useState<IPendingTransition | null>(null);

  const [previewOpen, setPreviewOpen] = React.useState(false);
  const [previewFile, setPreviewFile] = React.useState<FileResponse | null>(
    null,
  );
  const [previewLoading, setPreviewLoading] = React.useState(false);
  const previewUrl = usePdfBlobUrl(previewFile?.data);

  const saveTransmittal = useSaveTransmittal();
  const previewTransmittalPdf = usePreviewTransmittalPdf();
  const rotateExternalAccess = useRotateTransmittalExternalAccess();
  const uploadSignedFile = useUploadTransmittalTempFile();
  const uploadTempAttachment = useUploadTempAttachment();
  const downloadAttachment = useResolveAttachmentUrl();
  const updateAttachmentComment = useUpdateAttachmentComment();

  const revoker = useRevokeExternalLinks<TransmittalDto>({
    rotate: () =>
      rotateExternalAccess.mutateAsync({
        jobId: selectedJob.id,
        id: values.id ?? '',
      }),
    onRevoked: (dto) => {
      formik.resetForm({
        values: {
          ...values,
          documents: dto.documents ?? values.documents,
        },
      });
      onSaved?.(dto);
    },
    logLabel: 'Failed to revoke transmittal external links',
  });

  const isSigned = (a: ResourceAttachmentDto): boolean =>
    a.category === SIGNED_CATEGORY;
  const documents = values.documents ?? [];
  const attachmentFiles = documents.filter((a) => !isSigned(a));
  const signedFiles = documents.filter(isSigned);

  const applyFileChange = (
    nextForCategory: IAttachedFile[],
    signedSlice: boolean,
    change: IAttachmentChange,
  ) => {
    formik.setValues((v) => {
      const edits = applyAttachmentChange(
        { added: v.filesToAdd, removed: v.filesToRemove },
        change,
      );
      const others = (v.documents ?? []).filter((a) =>
        signedSlice ? !isSigned(a) : isSigned(a),
      );
      return {
        ...v,
        documents: [...(nextForCategory as ResourceAttachmentDto[]), ...others],
        filesToAdd: edits.added as ResourceAttachmentDto[],
        filesToRemove: edits.removed as ResourceAttachmentDto[],
      };
    });
  };

  const handleDocumentAttached = (attachment: ResourceAttachmentDto) => {
    formik.setValues((v) => ({
      ...v,
      documents: [...(v.documents ?? []), attachment],
      filesToAdd: [...v.filesToAdd, attachment],
    }));
  };

  const handleCommentSave = async (file: IAttachedFile, comment: string) => {
    const updated = await updateAttachmentComment
      .mutateAsync({
        jobId: selectedJob.id,
        attachmentId: file.id,
        comment,
      })
      .catch(() => null);
    if (!updated) return;
    formik.setValues((v) => ({
      ...v,
      documents: (v.documents ?? []).map((a) =>
        a.id === file.id ? { ...a, comment: updated.comment ?? comment } : a,
      ),
    }));
  };

  const handlePreviewPdf = async () => {
    if (!values.id) return;
    setPreviewOpen(true);
    setPreviewLoading(true);
    const file = await previewTransmittalPdf
      .mutateAsync({ jobId: selectedJob.id, id: values.id })
      .catch(() => undefined);
    if (file === undefined) setPreviewOpen(false);
    else setPreviewFile(file);
    setPreviewLoading(false);
  };

  const closePreview = () => {
    setPreviewOpen(false);
    setPreviewFile(null);
  };

  const buildRequest = (
    overrides: Partial<TransmittalRequestDto> = {},
  ): TransmittalRequestDto => ({
    id: values.id || undefined,
    title: values.title,
    status: values.status,
    stepIndex: values.stepIndex,
    notes: values.notes,
    dateSent: values.dateSent,
    externalContact: values.externalContact,
    submittedToContactId: values.submittedToContactId,
    workflowId: values.workflowId,
    assigneeUserId: values.assigneeUserId,
    filesToAdd: values.filesToAdd?.map((f) => ({ id: f.id })),
    filesToRemove: values.filesToRemove?.map((f) => ({ id: f.id })),
    saveOnly: false,
    notification: undefined,
    ...overrides,
  });

  const {
    submitWorkflow,
    voidWorkflow,
    saveOnly,
    error,
    fieldErrors,
    isSubmitting,
  } = useWorkflowSubmission<TransmittalDto, TransmittalRequestDto>(
    values,
    selectedJob.id,
    {
      mutations: { save: saveTransmittal },
      toRequest: (_vals, overrides) =>
        buildRequest({
          status: overrides?.status,
          stepIndex: overrides?.stepIndex,
          saveOnly: overrides?.saveOnly ?? false,
          notification: overrides?.notification,
        }),
      entityName: 'Transmittal',
      toast: {
        getEntityNumber: (t) => t.transmittalNumber ?? '',
      },
    },
    {
      onCreated: (result) => onCreated?.(result),
      onSaved: (result) => onSaved?.(result),
      setValues: (dto) => {
        formik.resetForm({
          values: {
            ...values,
            ...dto,
            documents: dto.documents ?? values.documents,
            filesToAdd: [],
            filesToRemove: [],
            workflow: dto.workflow ?? values.workflow,
            externalContact: values.externalContact,
          },
        });
      },
    },
  );

  const openComposerWithPreview = async (
    eventType: string,
    onFailure?: () => void,
  ): Promise<boolean> => {
    setComposerOpen(true);
    const preview = await composer.loadPreview(eventType);
    if (!preview) {
      setComposerOpen(false);
      onFailure?.();
      showErrorToast(
        'Unable to load the notification preview. Please try again.',
      );
      return false;
    }
    return true;
  };

  const { passesValidation, fieldError } =
    useWorkflowFormValidation<TransmittalFormValues>(
      formik,
      fieldErrors,
      TRANSMITTAL_FIELD_LABELS,
    );

  const handleSaveOnly = async () => {
    if (!(await passesValidation())) return;
    await saveOnly();
  };

  const onSubmitClick = async (status: string, nextStepIndex: number) => {
    if (!(await passesValidation())) return;

    const currentStepIndex = values.stepIndex ?? -1;
    const isForward = nextStepIndex === currentStepIndex + 1;
    const targetStep = values.workflow?.steps?.[nextStepIndex] ?? null;

    // The single external step is also the last step, so the composer must open
    // on it to send the client email (no isLastStep guard, no booking pass).
    if (
      shouldOpenTransitionComposer({
        isForward,
        hasId: !!values.id,
        targetStep,
      })
    ) {
      setPendingTransition({
        status,
        stepIndex: nextStepIndex,
        stepLabel: targetStep.label ?? '',
      });
      await openComposerWithPreview(`StepAdvanced:${nextStepIndex}`, () =>
        setPendingTransition(null),
      );
      return;
    }

    await submitWorkflow(status, nextStepIndex);
  };

  const submitTransition = async (request: NotificationCompositionRequest) => {
    if (!pendingTransition) return;
    try {
      const result = await submitWorkflow(
        pendingTransition.status,
        pendingTransition.stepIndex,
        false,
        { notification: request },
      );
      if (!result) {
        showErrorToast('Unable to submit the transmittal. Please try again.');
        return;
      }
      setComposerOpen(false);
      setPendingTransition(null);
    } catch (err: unknown) {
      logger.error('Failed to submit transmittal transition', { err });
      showErrorToast(getErrorMessage(err));
    }
  };

  const cancelTransition = () => {
    setComposerOpen(false);
    setPendingTransition(null);
    showToast('Submission canceled — status unchanged');
  };

  const customerNumber = selectedJob.customerNumber?.trim() ?? '';
  const stepEditable = isStepEditable({
    isAdmin,
    currentStep,
    status: values.status,
  });

  const showDetails = !activeSection || activeSection === 'details';
  const showInlineDocuments = !activeSection;
  const showDocumentsTab = activeSection === 'attachments';
  const showSigned = activeSection === 'signed';

  const documentSection = (
    <>
      <Group justify="space-between">
        <TransmittalDocumentPicker
          jobId={selectedJob.id}
          onAttached={handleDocumentAttached}
        />
        {values.id && attachmentFiles.some((f) => f.externalUrl) && (
          <SecondaryButton onClick={revoker.openModal}>
            Revoke links
          </SecondaryButton>
        )}
      </Group>
      <Input.Wrapper error={fieldError('documents')}>
        <FileDrop
          files={attachmentFiles}
          onChange={(files, change) => applyFileChange(files, false, change)}
          onCommentSave={handleCommentSave}
          onCopyLink={revoker.copyLink}
          uploadFile={(file) =>
            uploadTempAttachment.mutateAsync({ typeId: 'transmittal', file })
          }
          downloadFile={(id) => downloadAttachment.mutateAsync(id)}
        />
      </Input.Wrapper>
    </>
  );

  return (
    <Stack gap="md" pb="md">
      {showDetails && (
        <>
          <Paper>
            <Stack gap="md">
              <Group justify="space-between">
                <Text fw={600} size="lg">
                  Details
                </Text>
                {values.id && (
                  <SecondaryButton onClick={handlePreviewPdf}>
                    Preview PDF
                  </SecondaryButton>
                )}
              </Group>
              <Grid>
                <Grid.Col span={6}>
                  <TextInput label="Job" value={selectedJob.name} disabled />
                </Grid.Col>
                <Grid.Col span={6}>
                  <TextInput
                    label="Transmittal Number"
                    name="transmittalNumber"
                    value={values.transmittalNumber}
                    disabled
                    placeholder="Auto Generated"
                  />
                </Grid.Col>
              </Grid>
              {isAdmin && (
                <TextInput
                  label="Assigned Workflow"
                  description="(Admin Only)"
                  name="workflow"
                  value={values.workflow?.name}
                  disabled
                />
              )}
            </Stack>
          </Paper>
          <Paper>
            <Stack gap="md">
              <Text fw={600} size="lg">
                Transmittal
              </Text>
              <Grid>
                <Grid.Col span={6}>
                  <TextInput
                    label="Title"
                    name="title"
                    withAsterisk
                    value={values.title ?? ''}
                    disabled={!stepEditable}
                    maxLength={FIELD_LIMITS.singleLine}
                    error={fieldError('title')}
                    onBlur={formik.handleBlur}
                    onChange={(e) => setFieldValue('title', e.target.value)}
                  />
                </Grid.Col>
                {values.fromUser && (
                  <Grid.Col span={6}>
                    <TextInput
                      label="From"
                      name="from"
                      value={values.fromUser.fullName}
                      disabled
                    />
                  </Grid.Col>
                )}
              </Grid>
              <SimpleGrid cols={{ base: 1, sm: 2 }}>
                <SubmittedBySelector
                  label="Assigned To"
                  disabled={!stepEditable}
                  value={values.assigneeUserId ?? undefined}
                  onChange={(picked) =>
                    setFieldValue('assigneeUserId', picked?.id ?? undefined)
                  }
                />
                <ContactPicker
                  label="Submitting To"
                  allowCreate
                  disabled={!stepEditable}
                  customerNumber={customerNumber}
                  customerName={selectedJob.customerName ?? ''}
                  initialValue={submittingToContact}
                  onChange={(contact) => {
                    setSubmittingToContact(contact);
                    setFieldValue(
                      'submittedToContactId',
                      contact?.id ?? undefined,
                    );
                  }}
                />
              </SimpleGrid>
              <Textarea
                label="Notes"
                name="notes"
                disabled={!stepEditable}
                value={values.notes ?? ''}
                maxLength={FIELD_LIMITS.narrative}
                description={characterCount(
                  values.notes,
                  FIELD_LIMITS.narrative,
                )}
                inputWrapperOrder={['label', 'input', 'description', 'error']}
                error={fieldError('notes')}
                onBlur={formik.handleBlur}
                onChange={(e) => setFieldValue('notes', e.target.value)}
                placeholder="Enter notes..."
                minRows={3}
              />
              {showInlineDocuments && (
                <>
                  <Divider />
                  {documentSection}
                </>
              )}
            </Stack>
          </Paper>
          {values.externalContact && (
            <Paper>
              <Stack gap="md">
                <Text fw={600} size="lg">
                  Client Contact
                </Text>
                <SimpleGrid cols={{ base: 1, sm: 2 }}>
                  <TextInput
                    label="Name"
                    value={`${values.externalContact.first_name} ${values.externalContact.last_name}`}
                    disabled
                  />
                  <TextInput
                    label="Email"
                    value={values.externalContact.email}
                    disabled
                  />
                </SimpleGrid>
              </Stack>
            </Paper>
          )}
          <RelatedFeedFooter
            jobId={selectedJob.id}
            filter={{ transmittalId: values.id }}
            enabled={!!values.id}
          />
        </>
      )}

      {showDocumentsTab && (
        <Paper>
          <Stack gap="md">
            <Text fw={600} size="lg">
              Documents
            </Text>
            {documentSection}
          </Stack>
        </Paper>
      )}

      {showSigned && (
        <Paper>
          <Stack gap="md">
            <Text fw={600} size="lg">
              Signed Documents
            </Text>
            <FileDrop
              files={signedFiles}
              onChange={(files, change) =>
                applyFileChange(
                  files.map((f) =>
                    f.category === SIGNED_CATEGORY
                      ? f
                      : { ...f, category: SIGNED_CATEGORY },
                  ),
                  true,
                  change,
                )
              }
              onCommentSave={handleCommentSave}
              uploadFile={(file) =>
                uploadSignedFile.mutateAsync({
                  file,
                  category: SIGNED_CATEGORY,
                })
              }
              downloadFile={(id) => downloadAttachment.mutateAsync(id)}
            />
          </Stack>
        </Paper>
      )}

      {children}

      <WorkflowFormFooter
        dirty={formik.dirty}
        showButtons={!isTerminalStatus(values.status)}
        error={error}
        onSubmitClick={onSubmitClick}
        workflow={values.workflow ?? null}
        onSaveOnlyClick={handleSaveOnly}
        onVoidClick={voidWorkflow}
        stepIndex={values.stepIndex ?? -1}
        composerOpen={composerOpen}
        onComposerClose={cancelTransition}
        preview={composer.preview}
        jobUsers={jobUsers}
        customerNumber={customerNumber}
        composerSubmitting={isSubmitting}
        composerTitle={`${
          pendingTransition?.stepLabel ?? 'Transmittal'
        } — notification`}
        attachPdfLabel="Attach transmittal PDF"
        onSend={submitTransition}
        onSkip={submitTransition}
        onReloadPreview={composer.reloadPreview}
        previewOpen={previewOpen}
        onPreviewClose={closePreview}
        pdfTitle="Transmittal PDF"
        previewUrl={previewUrl}
        previewLoading={previewLoading}
        revokeOpen={revoker.modalOpen}
        onRevokeClose={revoker.closeModal}
        onRevokeConfirm={revoker.revoke}
        revoking={revoker.revoking}
        revokeEntityNoun="transmittal"
        revokeItemsNoun="documents"
      />
    </Stack>
  );
};
